import type {
  AppRegistryEntry,
  Incident,
  IncidentStatus,
  IncidentType,
  MailDashboard,
  MailMessagesPage,
  MailWindowStats,
  RecipientStats,
  SuppressionListRow,
} from "@feedback-hub/contract/browser";

import { getIdToken, signOut } from "./auth.js";
import { config } from "./config.js";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface IncidentPage {
  items: Incident[];
  cursor?: string;
}

export interface AppMetrics {
  days: number;
  totals: {
    incidents: number;
    ratings: number;
    bugs: number;
    withEmail: number;
  };
  satisfactionBySurface: Array<{
    surfaceId: string;
    up: number;
    down: number;
    rate: number | null;
  }>;
  bugIncidenceByRelease: Array<{ appVersion: string; count: number }>;
  topRecurring: Array<{
    dedupeKey: string;
    occurrences: number;
    ref: string;
    status: IncidentStatus;
  }>;
  timeToTriageMs: { p50: number | null; p90: number | null };
  timeToResolutionMs: { p50: number | null; p90: number | null };
  loopClosureRate: number | null;
  contactProvidedRate: number | null;
  reopenRate: number | null;
  wontFixRatio: number | null;
  spamRatio: number | null;
}

/** Sentinel appId for the combined "All applications" view. */
export const ALL_APPS = "*";

export interface ListIncidentsParams {
  /** A registered appId, or `ALL_APPS` for the combined view. */
  appId: string;
  status?: IncidentStatus | "";
  type?: IncidentType | "";
  surfaceId?: string;
  limit?: number;
  cursor?: string;
}

export interface BatchStatusItem {
  appId: string;
  incidentId: string;
}

export interface BatchStatusResult {
  status: IncidentStatus;
  applied: number;
  results: Array<{
    appId: string;
    incidentId: string;
    ok: boolean;
    ref?: string;
    status?: IncidentStatus;
    error?: string;
  }>;
}

// No stored session or a rejected token: clear it and reload so RequireAuth re-mounts fresh
// and shows the login form. There's no redirect flow to fall back into with native SRP auth.
function forceReauth(): void {
  signOut();
  window.location.reload();
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    forceReauth();
    throw new ApiError("Not signed in.", 401);
  }

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
  if (res.status === 401) {
    forceReauth();
    throw new ApiError("Session expired.", 401);
  }

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = { error: text };
    }
  }

  if (!res.ok) {
    const message =
      body && typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }

  return body as T;
}

export function listApps(): Promise<{ apps: AppRegistryEntry[] }> {
  return apiFetch("/v1/admin/apps");
}

export function listIncidents(params: ListIncidentsParams): Promise<IncidentPage> {
  const q = new URLSearchParams();
  q.set("appId", params.appId);
  if (params.status) q.set("status", params.status);
  if (params.type) q.set("type", params.type);
  if (params.surfaceId) q.set("surfaceId", params.surfaceId);
  if (params.limit) q.set("limit", String(params.limit));
  if (params.cursor) q.set("cursor", params.cursor);
  return apiFetch(`/v1/admin/incidents?${q.toString()}`);
}

export function batchUpdateStatus(
  items: BatchStatusItem[],
  status: IncidentStatus,
  note?: string,
): Promise<BatchStatusResult> {
  return apiFetch("/v1/admin/incidents/batch-status", {
    method: "POST",
    body: JSON.stringify({ items, status, note: note || undefined }),
  });
}

export function getIncident(
  appId: string,
  incidentId: string,
): Promise<{ incident: Incident }> {
  return apiFetch(
    `/v1/admin/incidents/${encodeURIComponent(appId)}/${encodeURIComponent(incidentId)}`,
  );
}

export function updateIncidentStatus(
  appId: string,
  incidentId: string,
  status: IncidentStatus,
  note?: string,
): Promise<{ incident: Incident }> {
  return apiFetch(
    `/v1/admin/incidents/${encodeURIComponent(appId)}/${encodeURIComponent(incidentId)}/status`,
    {
      method: "POST",
      body: JSON.stringify({ status, note: note || undefined }),
    },
  );
}

/**
 * Retries the submission auto-reply. Resolves with the outcome even when the send itself
 * failed again -- only a request that could not be attempted at all rejects.
 */
export function resendAutoReply(
  appId: string,
  incidentId: string,
): Promise<{ incident: Incident; emailStatus: "sent" | "failed" | "skipped" }> {
  return apiFetch(
    `/v1/admin/incidents/${encodeURIComponent(appId)}/${encodeURIComponent(incidentId)}/resend`,
    { method: "POST" },
  );
}

export function getMetrics(
  appId: string,
  days = 90,
): Promise<{ metrics: AppMetrics }> {
  const q = new URLSearchParams({ appId, days: String(days) });
  return apiFetch(`/v1/admin/metrics?${q.toString()}`);
}

// ---- Mail gateway dashboard (Phase 9) ------------------------------------------------------

export interface MailAppStats {
  appId: string;
  windows: MailWindowStats[];
}

export function getMailStats(): Promise<{ apps: MailAppStats[]; generatedAt: string }> {
  return apiFetch("/v1/admin/mail/stats");
}

export function getMailDashboard(): Promise<MailDashboard> {
  return apiFetch("/v1/admin/mail/dashboard");
}

export function getMailMessages(before?: string, days = 30): Promise<MailMessagesPage> {
  const q = new URLSearchParams({ days: String(days) });
  if (before) q.set("before", before);
  return apiFetch(`/v1/admin/mail/messages?${q.toString()}`);
}

export function getTopRecipients(limit = 50): Promise<{ recipients: RecipientStats[] }> {
  return apiFetch(`/v1/admin/mail/recipients?limit=${limit}`);
}

export function lookUpRecipient(email: string): Promise<{ recipient: RecipientStats | null }> {
  return apiFetch(`/v1/admin/mail/recipients?email=${encodeURIComponent(email)}`);
}

export function getMailSuppressions(
  limit = 100,
): Promise<{ suppressions: SuppressionListRow[] }> {
  return apiFetch(`/v1/admin/mail/suppressions?limit=${limit}`);
}

export function removeMailSuppression(
  email: string,
): Promise<{ removed: { email: string; reason: string } }> {
  return apiFetch("/v1/admin/mail/suppressions/remove", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}
